import React, { useMemo, useState } from "react";
import Modal, { Header, Content } from "_c/Modals";
import { useToggle } from "@/hooks/useToggle";
import { CircleButton } from "_c/button";
import PlusIcon from "_c/icon/Plus";
import { requiredMixin } from "@/styles/mixins";
import TextInput from "_c/form/TextInput";
import Empty from "_c/Empty";
import isempty from "lodash/isEmpty";
import orderBy from "lodash/orderBy";
import { useAllSpecsQuery } from "@/hooks/queries/useAllSpecsQuery";
import QueryRequest from "_c/QueryRequest";
import { Spin } from "_c/preloader";
const specsSpin = <Spin size="sm" centered={false} />;

const SpecSelect = ({ isDisabled, t, value, onChange, onBlur, ns }) => {
  const specsQuery = useAllSpecsQuery();
  const specs = specsQuery.data;
  const [isOpen, toggle] = useToggle();
  const onSelect = (spec) => {
    onChange(spec);
    toggle();
  };
  const specTitle = specs?.[value]?.title ?? "Unknown";
  return (
    <QueryRequest query={specsQuery} spin={specsSpin}>
      <div
        onClick={toggle}
        role="button"
        onBlur={onBlur}
        style={{
          pointerEvents: isDisabled ? "none" : "auto",
          opacity: isDisabled ? 0.5 : 1,
        }}
        css={`
          display: flex;
          align-items: center;
          cursor: pointer;
          color: var(--font-color-main);
          font-weight: 500;
        `}
      >
        <CircleButton as="div" greenish>
          <PlusIcon color="white" />
        </CircleButton>
        {value ? (
          <span
            css={`
              margin-inline-start: 10px;
            `}
          >
            {specTitle}
          </span>
        ) : (
          <span
            css={`
              margin-inline-start: 10px;
              ${requiredMixin};
            `}
          >
            {t("common:spec")}
          </span>
        )}
        {!isDisabled && (
          <span
            style={{ marginInlineStart: "auto", color: "var(--color-cta)" }}
          >
            {t("common:change")}
          </span>
        )}
      </div>
      <Modal
        css={`
          height: 100%;
        `}
        isOpen={isOpen}
        onClose={toggle}
      >
        <Header onClick={toggle}>{t("common:close")}</Header>
        <Content>
          <SpecModal specs={specs} ns={ns} onSelect={onSelect} t={t} />
        </Content>
      </Modal>
    </QueryRequest>
  );
};
const SpecModal = (props) => {
  const [search, setSearch] = useState("");
  const specs = useMemo(() => {
    if (!props.specs) return [];
    return orderBy(
      Object.entries(props.specs).map(([id, { title }]) => ({
        value: id,
        label: title,
      })),
      "label",
    );
  }, [props.specs]);
  const filteredSpecs = useMemo(() => {
    if (!search) return specs;
    const lower = search.toLowerCase();
    return specs.filter(({ label }) => label.toLowerCase().includes(lower));
  }, [specs, search]);
  return (
    <div
      css={`
        color: var(--font-color-main);
        input {
          margin-bottom: 15px;
        }
      `}
    >
      <TextInput
        autoFocus
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={props.t(`${props.ns}.spec_placeholder`)}
      />
      {isempty(filteredSpecs) ? (
        <Empty message={props.t("common:empty")} />
      ) : (
        <ul>
          {filteredSpecs.map((spec) => (
            <li
              onClick={() => props.onSelect(spec.value)}
              role="button"
              css={`
                background-color: var(--bg-elevate);
                padding: 10px;
                cursor: pointer;
                margin-bottom: 7px;
                border-radius: 9px;
              `}
              key={spec.value}
            >
              {spec.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
export default SpecSelect;
